import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSignalR } from './use-signalr';
import type { EventTimeline, TimelineBucket, EventTrends } from '../types/event';

interface DetectionsIngestedPayload {
  count: number;
  categories: Array<{ category: string; count: number }>;
  timestamp: string;
}

interface UseDetectionUpdatesOptions {
  /**
   * Called for every DetectionsIngested message, after the query cache has been patched.
   * See the note on useDeviceUpdates — the guided first-run flow completes on the first detection,
   * which is a push the server already makes.
   */
  onDetections?: (payload: DetectionsIngestedPayload) => void;
}

export function useDetectionUpdates(options: UseDetectionUpdatesOptions = {}) {
  const queryClient = useQueryClient();
  const { connection, status } = useSignalR({ hubPath: '/bff/hubs/devices' });

  const onDetectionsRef = React.useRef(options.onDetections);
  onDetectionsRef.current = options.onDetections;

  React.useEffect(() => {
    if (!connection) return;

    const handleDetectionsIngested = (payload: DetectionsIngestedPayload) => {
      // Append new buckets to timeline
      queryClient.setQueriesData<EventTimeline>(
        { queryKey: ['events', 'timeline'] },
        (old) => {
          if (!old) return old;
          const newBuckets: TimelineBucket[] = payload.categories.map((cat) => ({
            timestamp: payload.timestamp,
            count: cat.count,
            category: cat.category,
          }));
          return { buckets: [...old.buckets, ...newBuckets] };
        },
      );

      // Invalidate history list so new events appear
      queryClient.invalidateQueries({ queryKey: ['events', 'history'], refetchType: 'active' });

      // Increment trends counters
      queryClient.setQueriesData<EventTrends>(
        { queryKey: ['events', 'trends'] },
        (old) => {
          if (!old) return old;
          const updatedCategories = [...old.categories];
          for (const cat of payload.categories) {
            const idx = updatedCategories.findIndex((c) => c.category === cat.category);
            if (idx >= 0) {
              updatedCategories[idx] = {
                ...updatedCategories[idx],
                current: updatedCategories[idx].current + cat.count,
              };
            } else {
              updatedCategories.push({
                category: cat.category,
                current: cat.count,
                previous: 0,
                changePercent: 100,
              });
            }
          }
          return {
            ...old,
            totalEvents: old.totalEvents + payload.count,
            categories: updatedCategories,
          };
        },
      );

      onDetectionsRef.current?.(payload);
    };

    connection.on('DetectionsIngested', handleDetectionsIngested);
    return () => {
      connection.off('DetectionsIngested', handleDetectionsIngested);
    };
  }, [connection, queryClient]);

  return { connectionStatus: status };
}
